import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAdminUser, useUpdateUser, useUpdateUserStatus, useDeleteUser, useResetPassword, useAdminSites, useAssignUserToSite, useUnassignUserFromSite, useUpdateSitePermissions } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatDate } from '@/utils/format';

const roleVariant: Record<string, 'purple' | 'info' | 'default'> = { super_admin: 'purple', admin: 'info', store_owner: 'info', user: 'default', manager: 'default', staff: 'default' };
const statusVariant: Record<string, 'success' | 'warning' | 'error'> = { active: 'success', suspended: 'warning', inactive: 'error' };

const PERMISSION_KEYS = [
  'ordersView', 'ordersEdit', 'ordersRefund',
  'productsView', 'productsEdit',
  'customersView',
  'analyticsView', 'analyticsAdvanced',
  'notificationsEnabled',
  'settingsView', 'settingsEdit',
  'aiChat',
] as const;

export function AdminUserDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: user, isLoading, isError, refetch } = useAdminUser(userId ?? '');
  const updateUser = useUpdateUser();
  const updateStatus = useUpdateUserStatus();
  const deleteUser = useDeleteUser();
  const resetPassword = useResetPassword();
  const { data: allSitesData } = useAdminSites({ perPage: 100 });
  const assignSite = useAssignUserToSite();
  const unassignSite = useUnassignUserFromSite();
  const updatePerms = useUpdateSitePermissions();

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', role: '', phone: '', company: '', notes: '' });
  const [statusConfirm, setStatusConfirm] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [unassignConfirm, setUnassignConfirm] = useState<{ siteId: string; siteName: string } | null>(null);
  const [permsModal, setPermsModal] = useState<{ siteId: string; siteName: string; permissions: Record<string, boolean> } | null>(null);

  if (isLoading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full rounded-xl" /></div>;
  if (isError || !user) return <ErrorState onRetry={() => refetch()} />;

  // API returns nested: { user: {...}, subscription: {...}, sites: [...], stats: {...} }
  const profile = user.user ?? user;
  const subscription = user.subscription ?? profile.subscription;
  const userSites: Array<{ id: string; name: string; url: string; status: string; permissions?: Record<string, boolean> }> = user.sites ?? profile.sites ?? [];

  const openEdit = () => {
    setEditForm({ firstName: profile.firstName, lastName: profile.lastName, role: profile.role, phone: profile.phone ?? '', company: profile.company ?? '', notes: profile.notes ?? '' });
    setEditOpen(true);
  };

  const handleSave = async () => {
    await updateUser.mutateAsync({ userId: profile.id, data: editForm });
    setEditOpen(false);
  };

  const handleStatusChange = async (newStatus: string) => {
    await updateStatus.mutateAsync({ userId: profile.id, status: newStatus });
    setStatusConfirm(null);
  };

  const handleDelete = async () => {
    await deleteUser.mutateAsync(profile.id);
    setDeleteConfirm(false);
    navigate('/admin/users');
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 8) return;
    await resetPassword.mutateAsync({ userId: profile.id, newPassword });
    setResetOpen(false);
    setNewPassword('');
  };

  const handleAssignStore = async () => {
    if (!selectedSiteId) return;
    await assignSite.mutateAsync({ siteId: selectedSiteId, userId: profile.id });
    setAssignOpen(false);
    setSelectedSiteId('');
  };

  const handleUnassignStore = async () => {
    if (!unassignConfirm) return;
    await unassignSite.mutateAsync({ siteId: unassignConfirm.siteId, userId: profile.id });
    setUnassignConfirm(null);
  };

  const handleSavePermissions = async () => {
    if (!permsModal) return;
    await updatePerms.mutateAsync({ siteId: permsModal.siteId, userId: profile.id, permissions: permsModal.permissions });
    setPermsModal(null);
  };

  // Build list of sites not yet assigned
  const allSites = allSitesData?.items ?? allSitesData ?? [];
  const assignedSiteIds = new Set(userSites.map((s) => s.id));
  const availableSites = allSites.filter((s: { id: string }) => !assignedSiteIds.has(s.id));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} leftIcon={<ArrowLeftIcon className="h-4 w-4" />}>{t('common.back')}</Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.userDetail')}</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* User profile card */}
          <Card title={t('admin.userDetail')} action={<Button variant="ghost" size="sm" onClick={openEdit}>{t('common.edit')}</Button>}>
            <div className="flex items-center gap-4">
              <Avatar name={`${profile.firstName} ${profile.lastName}`} src={profile.avatarUrl} size="lg" />
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{profile.firstName} {profile.lastName}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{profile.email}</p>
                <div className="mt-1 flex gap-2">
                  <Badge label={t(`roles.${profile.role}`, { defaultValue: profile.role })} variant={roleVariant[profile.role] ?? 'default'} />
                  <Badge label={t(`common.${profile.status}`)} variant={statusVariant[profile.status] ?? 'default'} />
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-gray-500 dark:text-gray-400">{t('common.created')}</p><p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(profile.createdAt)}</p></div>
              {profile.lastLoginAt && <div><p className="text-gray-500 dark:text-gray-400">{t('common.lastLogin')}</p><p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(profile.lastLoginAt)}</p></div>}
              {profile.phone && <div><p className="text-gray-500 dark:text-gray-400">{t('common.phone')}</p><p className="font-medium text-gray-900 dark:text-gray-100">{profile.phone}</p></div>}
              {profile.company && <div><p className="text-gray-500 dark:text-gray-400">{t('common.company')}</p><p className="font-medium text-gray-900 dark:text-gray-100">{profile.company}</p></div>}
            </div>
            {subscription && (
              <div className="mt-4 rounded-lg border border-gray-100 p-3 dark:border-gray-800">
                <p className="text-xs font-semibold uppercase text-gray-400">{t('subscription.title')}</p>
                <div className="mt-1 flex flex-wrap gap-3 text-sm">
                  <span className="text-gray-900 dark:text-gray-100">{t(`subscription.plans.${subscription.plan}`, { defaultValue: subscription.plan })}</span>
                  <Badge label={t(`subscription.statuses.${subscription.status}`, { defaultValue: subscription.status })} variant={statusVariant[subscription.status] ?? 'default'} />
                  <span className="text-gray-500 dark:text-gray-400">{t('subscription.maxSites')}: {subscription.maxSites}</span>
                  {subscription.expiresAt && <span className="text-gray-500 dark:text-gray-400">{t('common.expires')}: {formatDate(subscription.expiresAt)}</span>}
                </div>
              </div>
            )}
          </Card>

          {/* Assigned stores */}
          <Card title={t('admin.assignedStores')} action={<Button variant="ghost" size="sm" onClick={() => setAssignOpen(true)} leftIcon={<PlusIcon className="h-4 w-4" />}>{t('admin.assignStore')}</Button>}>
            {userSites.length > 0 ? (
              <div className="space-y-2">
                {userSites.map((site) => (
                  <div key={site.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <span className={`h-2.5 w-2.5 rounded-full ${site.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{site.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{site.url}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => {
                        const perms: Record<string, boolean> = {};
                        PERMISSION_KEYS.forEach(k => { perms[k] = site.permissions?.[k] ?? false; });
                        setPermsModal({ siteId: site.id, siteName: site.name, permissions: perms });
                      }}>{t('admin.editPermissions')}</Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 dark:text-red-400" onClick={() => setUnassignConfirm({ siteId: site.id, siteName: site.name })}>{t('common.remove')}</Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">{t('admin.noAssignedStores')}</p>
            )}
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          <Card title={t('admin.changeStatus')}>
            <div className="space-y-2">
              {['active', 'suspended', 'inactive'].map((s) => (
                <Button key={s} variant={profile.status === s ? 'primary' : 'outline'} size="sm" className="w-full" disabled={profile.status === s} onClick={() => setStatusConfirm(s)}>
                  {t(`common.${s}`)}
                </Button>
              ))}
            </div>
          </Card>

          <Card title={t('common.actions')}>
            <div className="space-y-2">
              <Button variant="outline" size="sm" className="w-full" onClick={() => setResetOpen(true)}>{t('admin.resetPassword')}</Button>
              <Button variant="danger" size="sm" className="w-full" onClick={() => setDeleteConfirm(true)}>{t('admin.deleteUser')}</Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Edit user modal */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title={t('admin.editUser')} size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('settings.firstName')} value={editForm.firstName} onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} />
            <Input label={t('settings.lastName')} value={editForm.lastName} onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} />
          </div>
          <Select label={t('common.role')} options={[{ value: 'store_owner', label: t('roles.store_owner') }, { value: 'manager', label: t('roles.manager') }, { value: 'staff', label: t('roles.staff') }, { value: 'super_admin', label: t('roles.super_admin') }]} value={editForm.role} onChange={(v) => setEditForm((f) => ({ ...f, role: v }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('common.phone')} value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
            <Input label={t('common.company')} value={editForm.company} onChange={(e) => setEditForm((f) => ({ ...f, company: e.target.value }))} />
          </div>
          <Input label={t('admin.notes')} value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} loading={updateUser.isPending}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      {/* Status change confirmation */}
      <ConfirmDialog isOpen={!!statusConfirm} onClose={() => setStatusConfirm(null)} onConfirm={() => { if (statusConfirm) handleStatusChange(statusConfirm); }} title={t('admin.changeStatus')} message={`${t('admin.changeStatus')}: ${statusConfirm ? t(`common.${statusConfirm}`) : ''}`} confirmLabel={t('common.update')} confirmVariant={statusConfirm === 'suspended' || statusConfirm === 'inactive' ? 'danger' : 'primary'} loading={updateStatus.isPending} />

      {/* Delete user confirmation */}
      <ConfirmDialog isOpen={deleteConfirm} onClose={() => setDeleteConfirm(false)} onConfirm={handleDelete} title={t('admin.deleteUser')} message={t('admin.confirmDeleteUser')} confirmLabel={t('common.delete')} confirmVariant="danger" loading={deleteUser.isPending} />

      {/* Reset password modal */}
      <Modal isOpen={resetOpen} onClose={() => { setResetOpen(false); setNewPassword(''); }} title={t('admin.resetPassword')} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.resetPasswordDesc')}</p>
          <Input label={t('auth.newPassword')} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} error={newPassword.length > 0 && newPassword.length < 8 ? t('settings.passwordMinLength') : undefined} />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => { setResetOpen(false); setNewPassword(''); }}>{t('common.cancel')}</Button>
            <Button onClick={handleResetPassword} loading={resetPassword.isPending} disabled={newPassword.length < 8}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      {/* Assign store modal */}
      <Modal isOpen={assignOpen} onClose={() => { setAssignOpen(false); setSelectedSiteId(''); }} title={t('admin.assignStore')} size="sm">
        <div className="space-y-4">
          {availableSites.length > 0 ? (
            <>
              <Select label={t('admin.selectStore')} options={availableSites.map((s: { id: string; name: string; url: string }) => ({ value: s.id, label: `${s.name} (${s.url})` }))} value={selectedSiteId} onChange={setSelectedSiteId} placeholder={t('admin.selectStore')} />
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={() => { setAssignOpen(false); setSelectedSiteId(''); }}>{t('common.cancel')}</Button>
                <Button onClick={handleAssignStore} loading={assignSite.isPending} disabled={!selectedSiteId}>{t('common.assign')}</Button>
              </div>
            </>
          ) : (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">{allSites.length > 0 ? t('admin.allStoresAssigned') : t('sites.noSites')}</p>
          )}
        </div>
      </Modal>

      {/* Unassign store confirmation */}
      <ConfirmDialog isOpen={!!unassignConfirm} onClose={() => setUnassignConfirm(null)} onConfirm={handleUnassignStore} title={t('admin.unassignStore')} message={t('admin.confirmUnassignStore')} confirmLabel={t('common.remove')} confirmVariant="danger" loading={unassignSite.isPending} />

      {/* Edit permissions modal */}
      <Modal isOpen={!!permsModal} onClose={() => setPermsModal(null)} title={`${t('admin.editPermissions')} - ${permsModal?.siteName ?? ''}`} size="md">
        {permsModal && (
          <div className="space-y-4">
            <div className="space-y-2">
              {PERMISSION_KEYS.map((key) => (
                <label key={key} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <input
                    type="checkbox"
                    checked={permsModal.permissions[key] ?? false}
                    onChange={(e) => setPermsModal((prev) => prev ? { ...prev, permissions: { ...prev.permissions, [key]: e.target.checked } } : null)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t(`permissions.${key}`)}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setPermsModal(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleSavePermissions} loading={updatePerms.isPending}>{t('common.save')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
